import {TreeNodePage, TreeNodeCore} from '@/stores/apolloPlatform/cores/portalNavigation';
import {RouteRecordRaw} from 'vue-router';

export const pageRouteRecursive = (nodes: TreeNodePage[]) => {
  const records: RouteRecordRaw[] = [];
  for (const node of nodes) {
    // An external link is not a page: the menu opens `external_url` in a new tab, so nothing to route to.
    // A category only gets a page of its own when it has content, otherwise it's just a folder.
    if (
      node.meta.type !== 'cms.ExternalMenuLink' &&
      (node.meta.type !== 'cms.CategoryIndexPage' || node.meta.has_content)
    ) {
      const path = node.meta.url_path.slice(node.meta.url_path.indexOf('/', 1));
      const pageRoute: RouteRecordRaw = {
        name: `page-${node.meta.id}`,
        path,
        component: () => import('@/components/pages/PageView.vue'),
        props: () => ({meta: node.meta}),
        meta: {node},
        children: [],
      };
      if (
        node.meta.type === 'cms.DynamicPage' &&
        node.meta.url_path.match('portfolio|portefeuille') !== null
      ) {
        // N.B. nested <vue-router> in ProductCatalogueApp.vue & similar is broken.
        pageRoute.children.push({
          name: `page-${node.meta.id}-form`,
          path: 'update/:id?',
          alias: 'create/:id?',
          component: () => import('@/components/pages/PageView.vue'),
          props: () => ({meta: node.meta}),
          meta: {node, subpage: 'ProductCatalogueForm'},
        });
      }
      records.push(pageRoute);
    }
    if (node.children && node.children.length > 0) {
      records.push(...pageRouteRecursive(node.children)); // Recursively add children
    }
  }
  return records;
};

/** Routes for pages that are not in the menu: nothing to recurse into, they have no menu children. */
export const createHiddenRoutes = (nodes: (TreeNodeCore | TreeNodePage)[]): RouteRecordRaw[] =>
  nodes.flatMap((node) =>
    node.node_type === 'CoreNode' ? createCoreRoute(node) : pageRouteRecursive([node]),
  );

export const createCoreRoute = (node: TreeNodeCore): RouteRecordRaw[] => {
  let core: RouteRecordRaw | undefined = undefined;
  if (node.meta.type === 'cms.DashboardPage') {
    if (node.meta.slug === 'home') {
      // Home is served by the static /home route (available before nav loads).
      return pageRouteRecursive(node.children);
    }
    core = {
      name: `core-${node.meta.id}`,
      path: node.meta.slug,
      component: () => import('@/components/pages/DashboardView/DashboardView.vue'),
      props: () => ({node}),
      meta: {node},
    };
  } else if (node.meta.type === 'cms.NewsIndexPage') {
    // NewsIndexPage is a core node but renders as a page
    core = {
      name: `core-${node.meta.id}`,
      path: node.meta.slug,
      component: () => import('@/components/pages/PageView.vue'),
      props: () => ({meta: node.meta}),
      meta: {node},
    };
  } else if (node.meta.type !== 'cms.CoreIndexPage' || node.meta.has_content) {
    core = {
      name: `page-${node.meta.id}`,
      path: node.meta.slug,
      component: () => import('@/components/pages/PageView.vue'),
      props: () => ({meta: node.meta}),
      meta: {node},
    };
  }
  const children = pageRouteRecursive(node.children);
  return core !== undefined ? [core, ...children] : children;
};
